/*
 * Tipos do wire do dashboard (Tela 1 — landing em `/`).
 *
 * Espelham FIELMENTE o `DashboardOut` do backend (`flow/api/dashboard/*`,
 * BE 0d975e1). Regra de projeto INVIOLÁVEL: TODO valor monetário é `string` aqui
 * — nunca `number`. O wire entrega `NUMERIC(18,2)` serializado como string
 * decimal; convertê-lo para `number` em JS perderia precisão. O formatador em
 * `../formato/decimal` opera sobre a STRING, sem `Number`/`parseFloat`.
 *
 * As severidades de alerta são as strings EXATAS que o backend serializa
 * (rank informativa < baixa < media < alta < critica). A tupla `SEVERIDADES`
 * fixa a ordem de exibição e os rótulos pt-BR.
 */

/** Severidades de alerta — strings EXATAS do backend, em ordem crescente de rank. */
export const SEVERIDADES = [
  { valor: "informativa", rotulo: "Informativa" },
  { valor: "baixa", rotulo: "Baixa" },
  { valor: "media", rotulo: "Média" },
  { valor: "alta", rotulo: "Alta" },
  { valor: "critica", rotulo: "Crítica" },
] as const;

/** Uma severidade de alerta (string técnica do wire). */
export type Severidade = (typeof SEVERIDADES)[number]["valor"];

/** Janela analisada da projeção (datas ISO `YYYY-MM-DD`). */
export interface PeriodoOut {
  data_inicio: string;
  data_fim: string;
  dias_analisados: number;
}

/**
 * Saldo de HOJE (D+0). `saldo` é o BALANÇO do dia (entradas − saídas sobre o
 * saldo inicial), NÃO um "saldo líquido". Campos monetários são STRINGS decimais.
 */
export interface SaldoDia {
  data: string;
  saidas: string;
  entradas: string;
  saldo: string;
}

/**
 * Saldo PROJETADO de uma janela (7 dias / horizonte). `saldo_projetado` é o
 * balanço acumulado projetado ao fim da janela. Campos monetários são STRINGS.
 */
export interface SaldoJanela {
  saidas: string;
  entradas: string;
  saldo_projetado: string;
}

/** Bloco de saldo do dashboard: hoje + duas janelas projetadas. */
export interface SaldoBloco {
  hoje: SaldoDia;
  projecao_7d: SaldoJanela;
  projecao_horizonte: SaldoJanela;
}

/** Contagem de alertas ativos por severidade. */
export interface PorSeveridade {
  informativa: number;
  baixa: number;
  media: number;
  alta: number;
  critica: number;
}

/**
 * Resumo dos alertas ativos. `max_severidade` é a maior severidade entre os
 * alertas ativos, ou `null` quando não há alerta ativo.
 */
export interface AlertasResumo {
  total_ativos: number;
  por_severidade: PorSeveridade;
  max_severidade: string | null;
}

/**
 * Resumo da fila de conciliação. `ultimo_conciliado` é um datetime ISO (instante
 * do último movimento conciliado), ou `null` quando nada foi conciliado ainda.
 */
export interface FilaResumo {
  pendentes: number;
  ultimo_conciliado: string | null;
}

/**
 * Um dia de RISCO no período: data com pelo menos um alerta ativo, contagem de
 * alertas e a maior severidade do dia. `data` é ISO `YYYY-MM-DD`.
 */
export interface DiaDeRisco {
  data: string;
  alertas_count: number;
  max_severidade: string;
}

/** Payload completo do dashboard (`GET /api/dashboard`). */
export interface DashboardOut {
  periodo: PeriodoOut;
  saldo: SaldoBloco;
  alertas: AlertasResumo;
  fila_conciliacao: FilaResumo;
  dias_de_risco: DiaDeRisco[];
  contas_monitoradas: number;
  regras_ativas: number;
}
